// frontend/src/pages/AttachmentsPage.tsx - UPDATED FOR HOOKS/STORE INTEGRATION
// MODIFIED FUNCTION: Updated to use new API hooks, store, and utils following the pattern of NotificationsPage

import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn, formatDate, truncateText, formatFileSize } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';

// FIXED: Use entity-specific hooks following the pattern
import {
  useAttachments,
  useAttachment,
  useAttachmentStats,
  useAttachmentStorageAnalytics,
  useUploadAttachments,
  useUpdateAttachment,
  useDeleteAttachment,
  useDownloadAttachment,
  useGetAttachmentDownloadUrl,
  useTriggerAttachmentProcessing,
  useTriggerAttachmentVirusScan,
  useBatchDeleteAttachments,
  useBatchUpdateAttachments
} from '@/hooks/useAttachments';

// FIXED: Import useApi and useMutation for custom operations
import {
  useApi,
  useMutation
} from '@/hooks/useApi';

// FIXED: Import API types
import type {
  Attachment,
  AttachmentQueryParams,
  AttachmentUploadRequest,
  AttachmentUpdateRequest,
  AttachmentType,
  ProcessingStatus,
  VirusScanStatus,
  StorageTier,
  UserRole
} from '@/api/types';

import apiClient from '@/api';

// ===== UTILITY FUNCTIONS =====

// Debounced value hook
function useDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// ===== ERROR BOUNDARY =====

class AttachmentsErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error('AttachmentsPage Error Boundary:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('AttachmentsPage Error:', error, errorInfo);
    
    // Report error to app store for global error handling
    const { setGlobalError } = useAppStore.getState();
    setGlobalError(`Attachments Page Error: ${error.message}`);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Something went wrong
              </h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
              <button
                className="mt-2 text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
                onClick={() => window.location.reload()}
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ===== INTERFACES =====

interface AttachmentStats {
  totalAttachments: number;
  totalSize: number;
  byType: Record<AttachmentType, number>;
  byProcessingStatus: Record<ProcessingStatus, number>;
  byVirusScanStatus: Record<VirusScanStatus, number>;
  downloadCount: number;
  storageBreakdown: {
    emailAttachments: number;
    correspondenceAttachments: number;
    responseAttachments: number;
    staticDocuments: number;
  };
}

interface UploadProgress {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  result?: Attachment;
}

interface BatchOperationRequest {
  action: 'delete' | 'updateVisibility' | 'triggerProcessing' | 'updateTags';
  attachmentIds: string[];
  options?: {
    isPublic?: boolean;
    tags?: string[];
    description?: string;
  };
}

// ===== ATTACHMENT CARD COMPONENT =====
const AttachmentCard = memo<{
  attachment: Attachment;
  onDownload: (id: string) => void;
  onUpdate: (attachment: Attachment, updates: AttachmentUpdateRequest) => void;
  onDelete: (id: string) => void;
  onTriggerProcessing: (id: string) => void;
  onTriggerVirusScan: (id: string) => void;
  onGetPresignedUrl: (id: string) => void;
}>(({ attachment, onDownload, onUpdate, onDelete, onTriggerProcessing, onTriggerVirusScan, onGetPresignedUrl }) => {
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    filename: attachment.filename,
    isPublic: attachment.isPublic,
    description: attachment.description || '',
    tags: attachment.tags || []
  });

  const getFileIcon = useCallback((contentType: string): JSX.Element => {
    if (contentType.startsWith('image/')) {
      return (
        <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    } else if (contentType.includes('pdf')) {
      return (
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    } else if (contentType.includes('word') || contentType.includes('document')) {
      return (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    } else if (contentType.includes('spreadsheet') || contentType.includes('excel')) {
      return (
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11h8M8 15h8" />
        </svg>
      );
    } else {
      return (
        <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }
  }, []);

  const getProcessingStatusColor = useCallback((status?: ProcessingStatus): string => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'processing': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'error': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'pending': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  }, []);

  const getVirusScanStatusColor = useCallback((status?: VirusScanStatus): string => {
    switch (status) {
      case 'clean': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'infected': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'error': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300';
      case 'pending': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  }, []);

  const attachmentId = (attachment as any)._id || attachment.id || '';

  // Event handlers
  const handleDownload = useCallback(() => onDownload(attachmentId), [onDownload, attachmentId]);
  const handleDelete = useCallback(() => onDelete(attachmentId), [onDelete, attachmentId]);
  const handleTriggerProcessing = useCallback(() => onTriggerProcessing(attachmentId), [onTriggerProcessing, attachmentId]);
  const handleTriggerVirusScan = useCallback(() => onTriggerVirusScan(attachmentId), [onTriggerVirusScan, attachmentId]);
  const handleGetPresignedUrl = useCallback(() => onGetPresignedUrl(attachmentId), [onGetPresignedUrl, attachmentId]);

  const handleSaveEdit = useCallback(() => {
    onUpdate(attachment, {
      filename: editData.filename,
      isPublic: editData.isPublic,
      description: editData.description,
      tags: editData.tags
    });
    setIsEditing(false);
  }, [onUpdate, attachment, editData]);

  const handleCancelEdit = useCallback(() => {
    setEditData({
      filename: attachment.filename,
      isPublic: attachment.isPublic,
      description: attachment.description || '',
      tags: attachment.tags || []
    });
    setIsEditing(false);
  }, [attachment]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 transition-all duration-200 hover:shadow-md">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className="flex-shrink-0">
            {getFileIcon(attachment.contentType)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {isEditing ? (
                  <Input
                    value={editData.filename}
                    onChange={(e) => setEditData(prev => ({ ...prev, filename: e.target.value }))}
                    className="text-sm"
                  />
                ) : (
                  attachment.filename
                )}
              </h3>
              {attachment.isPublic && (
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300 rounded-full">
                  Public
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
              <span>{formatFileSize(attachment.size)}</span>
              <span>•</span>
              <span>{attachment.contentType}</span>
              <span>•</span>
              <span>{formatDate(attachment.createdAt)}</span>
              {(attachment.downloadCount || 0) > 0 && (
                <>
                  <span>•</span>
                  <span>{attachment.downloadCount || 0} downloads</span>
                </>
              )}
            </div>
            <div className="flex items-center space-x-2 mt-1">
              <span className={cn(
                'px-2 py-1 text-xs font-medium rounded-full',
                getProcessingStatusColor(attachment.processingStatus)
              )}>
                {attachment.processingStatus || 'unknown'}
              </span>
              <span className={cn(
                'px-2 py-1 text-xs font-medium rounded-full',
                getVirusScanStatusColor(attachment.virusScanStatus)
              )}>
                {attachment.virusScanStatus || 'unknown'}
              </span>
            </div>
          </div>
        </div>

        {/* Action Menu */}
        <div className="flex items-center space-x-1 ml-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            className="text-xs"
            title="Download"
          >
            ⬇
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleGetPresignedUrl}
            className="text-xs"
            title="Get Link"
          >
            🔗
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs"
            title="Toggle Details"
          >
            {isExpanded ? '−' : '+'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(!isEditing)}
            className="text-xs"
            title="Edit"
          >
            ✏
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            title="Delete"
          >
            🗑
          </Button>
        </div>
      </div>

      {/* Description */}
      {(attachment.description || isEditing) && (
        <div className="mb-3">
          {isEditing ? (
            <Input
              placeholder="Description..."
              value={editData.description}
              onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
              className="text-sm"
            />
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {attachment.description}
            </p>
          )}
        </div>
      )}

      {/* Edit Actions */}
      {isEditing && (
        <div className="mb-3 flex items-center space-x-2">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={editData.isPublic}
              onChange={(e) => setEditData(prev => ({ ...prev, isPublic: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Public</span>
          </label>
          <Button onClick={handleSaveEdit} size="sm" variant="primary">
            Save
          </Button>
          <Button onClick={handleCancelEdit} size="sm" variant="outline">
            Cancel
          </Button>
        </div>
      )}

      {/* Expanded Details */}
      {isExpanded && (
        <div className="mt-4 space-y-4 border-t border-gray-200 dark:border-gray-600 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">File Information</h4>
              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <div>Hash: {truncateText(attachment.fileHash || '', 20)}</div>
                <div>S3 URL: {attachment.s3Url ? 'Available' : 'Not available'}</div>
                {attachment.extractedContent && (
                  <div>Text Extracted: Yes ({attachment.extractedContent.length} chars)</div>
                )}
                {attachment.lastAccessedAt && (
                  <div>Last Accessed: {formatDate(attachment.lastAccessedAt)}</div>
                )}
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Processing</h4>
              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <div>Status: {attachment.processingStatus}</div>
                <div>Virus Scan: {attachment.virusScanStatus}</div>
                <div>Created: {formatDate(attachment.createdAt)}</div>
                {attachment.updatedAt && (
                  <div>Updated: {formatDate(attachment.updatedAt)}</div>
                )}
              </div>
              <div className="mt-2 space-x-2">
                {attachment.processingStatus !== 'completed' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTriggerProcessing}
                    className="text-xs"
                  >
                    Reprocess
                  </Button>
                )}
                {attachment.virusScanStatus !== 'clean' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTriggerVirusScan}
                    className="text-xs"
                  >
                    Scan Virus
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Tags */}
          {attachment.tags && attachment.tags.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tags</h4>
              <div className="flex flex-wrap gap-1">
                {attachment.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* References */}
          {(attachment.emailId || attachment.correspondenceId || attachment.responseId) && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">References</h4>
              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                {attachment.emailId && <div>Email: {truncateText(attachment.emailId, 20)}</div>}
                {attachment.correspondenceId && <div>Correspondence: {truncateText(attachment.correspondenceId, 20)}</div>}
                {attachment.responseId && <div>Response: {truncateText(attachment.responseId, 20)}</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

AttachmentCard.displayName = 'AttachmentCard';

// ===== FILE UPLOAD COMPONENT =====
const FileUploadZone: React.FC<{
  onFilesSelected: (files: FileList) => void;
  uploading: boolean;
  uploadProgress: UploadProgress[];
}> = ({ onFilesSelected, uploading, uploadProgress }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onFilesSelected(files);
    }
  }, [onFilesSelected]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onFilesSelected(files);
    }
    // Reset the input so the same file can be selected again
    if (e.target) {
      e.target.value = '';
    }
  }, [onFilesSelected]);

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      <div
        className={cn(
          "border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer",
          isDragOver
            ? "border-blue-400 bg-blue-50 dark:bg-blue-900/20"
            : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="flex flex-col items-center space-y-4">
          <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <div>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-2">
              {uploading ? 'Uploading files...' : 'Drop files here or click to upload'}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Support for PDF, DOC, DOCX, XLS, XLSX, Images and more (Max 10MB per file)
            </p>
          </div>
          
          <Button
            variant="primary"
            disabled={uploading}
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            {uploading ? 'Uploading...' : 'Choose Files'}
          </Button>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.zip,.rar,.csv"
        />
      </div>

      {/* Upload Progress */}
      {uploadProgress.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Upload Progress
          </h4>
          {uploadProgress.map((progress, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {progress.file.name}
                </span>
                <span className={cn(
                  "text-xs font-medium px-2 py-1 rounded",
                  progress.status === 'completed' ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300" :
                  progress.status === 'error' ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300" :
                  progress.status === 'uploading' ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300" :
                  "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300"
                )}>
                  {progress.status}
                </span>
              </div>
              {progress.status === 'uploading' && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress.progress}%` }}
                  />
                </div>
              )}
              {progress.error && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                  {progress.error}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== MAIN ATTACHMENTS PAGE COMPONENT =====
export const AttachmentsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [contentTypeFilter, setContentTypeFilter] = useState<string>('all');
  const [processingStatusFilter, setProcessingStatusFilter] = useState<string>('all');
  const [virusScanStatusFilter, setVirusScanStatusFilter] = useState<string>('all');
  const [publicFilter, setPublicFilter] = useState<'all' | 'public' | 'private'>('all');
  const [attachmentTypeFilter, setAttachmentTypeFilter] = useState<string>('all');
  const [selectedAttachments, setSelectedAttachments] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [showUploadZone, setShowUploadZone] = useState(false);

  const { getSafeUser } = useAuthStore();
  const { setGlobalError, clearGlobalError } = useAppStore();
  const { confirm } = useConfirmation();
  const currentUser = getSafeUser();

  // Debounced search term
  const debouncedSearchTerm = useDebounced(searchTerm, 300);

  // Query params following pattern from other pages
  const queryParams = useMemo((): AttachmentQueryParams => {
    const params: AttachmentQueryParams = {
      page: currentPage,
      limit: 20,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    };
    
    if (debouncedSearchTerm?.trim()) {
      params.search = debouncedSearchTerm.trim();
    }
    
    if (contentTypeFilter && contentTypeFilter !== 'all') {
      params.contentType = contentTypeFilter;
    }
    
    if (processingStatusFilter && processingStatusFilter !== 'all') {
      params.processingStatus = processingStatusFilter as ProcessingStatus;
    }
    
    if (virusScanStatusFilter && virusScanStatusFilter !== 'all') {
      params.virusScanStatus = virusScanStatusFilter as VirusScanStatus;
    }
    
    if (publicFilter !== 'all') {
      params.isPublic = publicFilter === 'public';
    }
    
    if (attachmentTypeFilter && attachmentTypeFilter !== 'all') {
      params.attachmentType = attachmentTypeFilter as AttachmentType;
    }
    
    return params;
  }, [debouncedSearchTerm, contentTypeFilter, processingStatusFilter, virusScanStatusFilter, publicFilter, attachmentTypeFilter, currentPage]);

  // Load attachments using new API pattern
  const { 
    data: attachmentsResponse, 
    loading: attachmentsLoading, 
    error: attachmentsError,
    refetch: refreshAttachments
  } = useAttachments(queryParams);

  // Load attachment stats
  const { 
    data: statsResponse,
    refetch: refreshStats
  } = useAttachmentStats();

  // Error handling
  useEffect(() => {
    if (attachmentsError) {
      setGlobalError(`Attachments loading error: ${attachmentsError}`);
    } else {
      clearGlobalError();
    }
  }, [attachmentsError, setGlobalError, clearGlobalError]);

  // Mutation hooks
  const uploadMutation = useUploadAttachments({
    onSuccess: (result) => {
      refreshAttachments();
      refreshStats();
      setUploadProgress([]);
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Upload failed: ${error.message || error}`);
    }
  });

  const updateMutation = useUpdateAttachment({
    onSuccess: () => {
      refreshAttachments();
      refreshStats();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Update failed: ${error.message || error}`);
    }
  });

  const deleteMutation = useDeleteAttachment({
    onSuccess: () => {
      refreshAttachments();
      refreshStats();
      setSelectedAttachments(prev => prev.filter(id => id !== prev[0])); // Remove the deleted one
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Delete failed: ${error.message || error}`);
    }
  });

  const downloadMutation = useDownloadAttachment({
    onSuccess: (blob, variables) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = variables || 'attachment';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
    onError: (error: any) => {
      setGlobalError(`Download failed: ${error.message || error}`);
    }
  });

  const getPresignedUrlMutation = useGetAttachmentDownloadUrl({
    onSuccess: (result) => {
      // Copy URL to clipboard
      navigator.clipboard.writeText(result.url).then(() => {
        // Show success message
      });
    },
    onError: (error: any) => {
      setGlobalError(`Failed to get download URL: ${error.message || error}`);
    }
  });

  const triggerProcessingMutation = useTriggerAttachmentProcessing({
    onSuccess: () => {
      refreshAttachments();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Processing trigger failed: ${error.message || error}`);
    }
  });

  const triggerVirusScanMutation = useTriggerAttachmentVirusScan({
    onSuccess: () => {
      refreshAttachments();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Virus scan failed: ${error.message || error}`);
    }
  });

  const batchDeleteMutation = useBatchDeleteAttachments({
    onSuccess: () => {
      refreshAttachments();
      refreshStats();
      setSelectedAttachments([]);
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Batch delete failed: ${error.message || error}`);
    }
  });

  // Extract data from responses
  const attachments = attachmentsResponse?.data || [];
  const pagination = attachmentsResponse?.pagination;
  const stats: AttachmentStats = statsResponse || {
    totalAttachments: 0,
    totalSize: 0,
    byType: {} as Record<AttachmentType, number>,
    byProcessingStatus: {} as Record<ProcessingStatus, number>,
    byVirusScanStatus: {} as Record<VirusScanStatus, number>,
    downloadCount: 0,
    storageBreakdown: {
      emailAttachments: 0,
      correspondenceAttachments: 0,
      responseAttachments: 0,
      staticDocuments: 0
    }
  };

  // Event handlers
  const handleDownload = useCallback(async (id: string) => {
    try {
      downloadMutation.mutate(id);
    } catch (error) {
      // Error handled by mutation onError
    }
  }, [downloadMutation]);

  const handleUpdate = useCallback(async (attachment: Attachment, updates: AttachmentUpdateRequest) => {
    try {
      const attachmentId = (attachment as any)._id || attachment.id;
      updateMutation.mutate({ id: attachmentId, data: updates });
    } catch (error) {
      // Error handled by mutation onError
    }
  }, [updateMutation]);

  const handleDelete = useCallback(async (id: string) => {
    const confirmed = await confirm({
      title: 'Delete Attachment',
      message: 'Are you sure you want to delete this attachment? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        deleteMutation.mutate(id);
      } catch (error) {
        // Error handled by mutation onError
      }
    }
  }, [deleteMutation, confirm]);

  const handleTriggerProcessing = useCallback(async (id: string) => {
    try {
      triggerProcessingMutation.mutate(id);
    } catch (error) {
      // Error handled by mutation onError
    }
  }, [triggerProcessingMutation]);

  const handleTriggerVirusScan = useCallback(async (id: string) => {
    try {
      triggerVirusScanMutation.mutate(id);
    } catch (error) {
      // Error handled by mutation onError
    }
  }, [triggerVirusScanMutation]);

  const handleGetPresignedUrl = useCallback(async (id: string) => {
    try {
      getPresignedUrlMutation.mutate(id);
    } catch (error) {
      // Error handled by mutation onError
    }
  }, [getPresignedUrlMutation]);

  const handleBatchDelete = useCallback(async () => {
    if (selectedAttachments.length === 0) {
      setGlobalError('No attachments selected');
      return;
    }
    
    const confirmed = await confirm({
      title: 'Delete Attachments',
      message: `Are you sure you want to delete ${selectedAttachments.length} attachments? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        batchDeleteMutation.mutate(selectedAttachments);
      } catch (error) {
        // Error handled by mutation onError
      }
    }
  }, [selectedAttachments, batchDeleteMutation, setGlobalError, confirm]);

  const handleSelectAll = useCallback(() => {
    if (selectedAttachments.length === attachments.length) {
      setSelectedAttachments([]);
    } else {
      setSelectedAttachments(attachments.map(a => (a as any)._id || a.id || '').filter(Boolean));
    }
  }, [selectedAttachments.length, attachments]);

  const handleSelectAttachment = useCallback((id: string) => {
    setSelectedAttachments(prev => 
      prev.includes(id) 
        ? prev.filter(attachmentId => attachmentId !== id)
        : [...prev, id]
    );
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    setSelectedAttachments([]); // Clear selection when changing pages
  }, []);

  const handleFilesSelected = useCallback(async (files: FileList) => {
    const fileArray = Array.from(files);
    
    // Initialize progress tracking
    const initialProgress: UploadProgress[] = fileArray.map(file => ({
      file,
      progress: 0,
      status: 'pending'
    }));
    
    setUploadProgress(initialProgress);

    try {
      const uploadRequest: AttachmentUploadRequest = {
        files: fileArray,
        attachmentType: 'static',
        isPublic: false
      };

      uploadMutation.mutate(uploadRequest);
    } catch (error) {
      // Error handled by mutation onError
      setUploadProgress(prev => prev.map(p => ({
        ...p,
        status: 'error',
        error: 'Upload failed'
      })));
    }
  }, [uploadMutation]);

  // Page actions
  const actions = useMemo(() => {
    const actionButtons = [];

    actionButtons.push(
      <Button
        key="upload"
        onClick={() => setShowUploadZone(!showUploadZone)}
        variant="primary"
      >
        {showUploadZone ? 'Hide Upload' : 'Upload Files'}
      </Button>
    );

    if (selectedAttachments.length > 0) {
      actionButtons.push(
        <Button
          key="batch-delete"
          onClick={handleBatchDelete}
          variant="outline"
          className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20"
          disabled={batchDeleteMutation.loading}
        >
          Delete ({selectedAttachments.length})
        </Button>
      );
    }

    return actionButtons;
  }, [showUploadZone, selectedAttachments.length, handleBatchDelete, batchDeleteMutation.loading]);

  // Permission check
  const hasAccess = !!currentUser;

  if (!hasAccess) {
    return (
      <PageLayout title="Attachments" subtitle="Access Restricted">
        <div className="rounded-md bg-yellow-50 dark:bg-yellow-900/20 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Authentication Required
              </h3>
              <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                <p>Please log in to view and manage attachments.</p>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <AttachmentsErrorBoundary>
      <PageLayout 
        title="Attachments"
        subtitle={`Manage and organize file attachments • ${stats.totalAttachments} files • ${formatFileSize(stats.totalSize)}`}
        actions={actions}
        loading={attachmentsLoading}
      >
        <div className="space-y-6">
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.totalAttachments}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Total Files
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {formatFileSize(stats.totalSize)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Total Size
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.downloadCount}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Total Downloads
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {Object.values(stats.byProcessingStatus || {}).filter((_, index) =>
                  Object.keys(stats.byProcessingStatus || {})[index] === 'completed'
                ).reduce((sum, val) => sum + val, 0)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Processed
              </div>
            </div>
          </div>

          {/* Upload Zone */}
          {showUploadZone && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <FileUploadZone
                onFilesSelected={handleFilesSelected}
                uploading={uploadMutation.loading}
                uploadProgress={uploadProgress}
              />
            </div>
          )}

          {/* Filters */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Search Files
                </label>
                <Input
                  id="search"
                  type="text"
                  placeholder="Search by filename or content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Content Type Filter */}
              <div>
                <label htmlFor="content-type-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  File Type
                </label>
                <select
                  id="content-type-filter"
                  value={contentTypeFilter}
                  onChange={(e) => setContentTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Types</option>
                  <option value="application/pdf">PDF</option>
                  <option value="image/">Images</option>
                  <option value="application/vnd.ms-excel">Excel</option>
                  <option value="application/msword">Word</option>
                  <option value="text/">Text</option>
                </select>
              </div>

              {/* Processing Status Filter */}
              <div>
                <label htmlFor="processing-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Processing
                </label>
                <select
                  id="processing-filter"
                  value={processingStatusFilter}
                  onChange={(e) => setProcessingStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="error">Error</option>
                </select>
              </div>

              {/* Virus Scan Filter */}
              <div>
                <label htmlFor="virus-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Virus Scan
                </label>
                <select
                  id="virus-filter"
                  value={virusScanStatusFilter}
                  onChange={(e) => setVirusScanStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="clean">Clean</option>
                  <option value="infected">Infected</option>
                  <option value="error">Error</option>
                </select>
              </div>

              {/* Public/Private Filter */}
              <div>
                <label htmlFor="public-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Visibility
                </label>
                <select
                  id="public-filter"
                  value={publicFilter}
                  onChange={(e) => setPublicFilter(e.target.value as 'all' | 'public' | 'private')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All</option>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>
          </div>

          {/* Attachments List */}
          {attachmentsLoading ? (
            <div className="text-center py-12">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-gray-500 dark:text-gray-400">Loading attachments...</p>
            </div>
          ) : attachmentsError ? (
            <ErrorAlert 
              message={attachmentsError}
              onClose={() => clearGlobalError()}
            />
          ) : attachments.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {searchTerm || contentTypeFilter !== 'all' || processingStatusFilter !== 'all' || publicFilter !== 'all'
                  ? 'No attachments found' 
                  : 'No attachments yet'
                }
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {searchTerm || contentTypeFilter !== 'all' || processingStatusFilter !== 'all' || publicFilter !== 'all'
                  ? 'Try adjusting your search criteria.'
                  : 'Upload some files to get started!'
                }
              </p>
            </div>
          ) : (
            <>
              {/* Selection Header */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedAttachments.length === attachments.length && attachments.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {selectedAttachments.length > 0 
                          ? `${selectedAttachments.length} selected`
                          : 'Select all'
                        }
                      </span>
                    </label>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {attachments.length} attachment{attachments.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>

              {/* Attachments */}
              <div className="space-y-4">
                {attachments.map((attachment) => {
                  const attachmentId = (attachment as any)._id || attachment.id || '';
                  return (
                    <div key={attachmentId} className="relative">
                      <div className="absolute left-2 top-2 z-10">
                        <input
                          type="checkbox"
                          checked={selectedAttachments.includes(attachmentId)}
                          onChange={() => handleSelectAttachment(attachmentId)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="pl-8">
                        <AttachmentCard
                          attachment={attachment}
                          onDownload={handleDownload}
                          onUpdate={handleUpdate}
                          onDelete={handleDelete}
                          onTriggerProcessing={handleTriggerProcessing}
                          onTriggerVirusScan={handleTriggerVirusScan}
                          onGetPresignedUrl={handleGetPresignedUrl}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} total)
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage <= 1}
                        variant="outline"
                        size="sm"
                      >
                        Previous
                      </Button>
                      <Button
                        onClick={() => handlePageChange(Math.min(pagination.totalPages, currentPage + 1))}
                        disabled={currentPage >= pagination.totalPages}
                        variant="outline"
                        size="sm"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </PageLayout>
    </AttachmentsErrorBoundary>
  );
};